/**
 * Penderyn Booking API Client
 */
const PenderynAPI = (function () {
  function getBaseURL() {
    return (window.PENDERYN_BOOKING_API || 'http://localhost:3000/api').replace(/\/$/, '');
  }

  async function request(endpoint, options = {}) {
    const url = getBaseURL() + endpoint;
    const resp = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(body.message || `API error ${resp.status}`);
    }
    if (resp.status === 204) return null;
    var json = await resp.json();
    // Unwrap {success, data} envelope and normalize _id to id
    var result = json.data !== undefined ? json.data : json;
    return normalizeIds(result);
  }

  function normalizeIds(obj) {
    if (Array.isArray(obj)) return obj.map(normalizeIds);
    if (obj && typeof obj === 'object') {
      var out = {};
      for (var k in obj) {
        if (k === '_id' && !('id' in obj)) out.id = obj[k];
        out[k] = (typeof obj[k] === 'object' && obj[k] !== null) ? normalizeIds(obj[k]) : obj[k];
      }
      return out;
    }
    return obj;
  }

  return {
    getLocations() {
      return request('/locations');
    },

    getProducts(locationId) {
      return request('/products?location=' + encodeURIComponent(locationId));
    },

    getSessions(start, end, locationId, productId) {
      const params = new URLSearchParams({ start, end, location: locationId, product: productId });
      return request('/sessions?' + params.toString());
    },

    checkSession(productId, date, time, guests) {
      const params = new URLSearchParams({ product: productId, date, time, guests });
      return request('/sessions/check?' + params.toString());
    },

    createReservation(data) {
      return request('/reservations', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    releaseReservation(holdToken) {
      return request('/reservations/' + encodeURIComponent(holdToken), {
        method: 'DELETE',
      });
    },

    /**
     * Check a promo code, for the experience being booked.
     *
     * The product matters: a partner's masterclass code is not meant to work
     * on a tour, and without saying which experience this is, the widget would
     * show "applied" and the booking would then be refused.
     */
    validatePromoCode(code, productId, locationId) {
      var q = [];
      if (productId) q.push('product=' + encodeURIComponent(productId));
      if (locationId) q.push('location=' + encodeURIComponent(locationId));
      return request('/promo-codes/validate/' + encodeURIComponent(code) + (q.length ? '?' + q.join('&') : ''));
    },
  };
})();
/**
 * Shopify Cart Integration
 *
 * Each ticket type is its own Shopify variant (its own price), so a booking
 * adds one cart line per selected ticket type. Every line carries the same
 * booking metadata; the API's orders/paid webhook groups lines by
 * `_booking_hold_token` back into a single booking.
 *
 * The variant id comes from the booking system, on the ticket type, because the
 * same ticket is priced differently at different sites. The booking system
 * creates the variant when the experience is saved, so a new experience is
 * sellable as soon as Penderyn has set it up.
 *
 * `window.PENDERYN_TICKET_VARIANTS` is the fallback for experiences saved
 * before that existed, and is kept so nothing that works today stops working:
 *   window.PENDERYN_TICKET_VARIANTS = { "swansea-distillery-tour": { adult: 5811... } }
 */
const PenderynCart = (function () {
  /**
   * The Shopify variant to put in the cart for one ticket.
   *
   * The booking system's own id wins. The theme map is consulted only when it
   * has none, which is what keeps experiences saved before the booking system
   * started creating variants working untouched.
   */
  function variantIdFor(ticket, forExperience) {
    return ticket.shopifyVariantId || (forExperience || {})[ticket.slug] || '';
  }

  async function addToCart(booking) {
    const variantMap = window.PENDERYN_TICKET_VARIANTS || {};

    const forExperience = variantMap[booking.productSlug] || {};

    const items = booking.tickets
      .filter(function (t) { return t.qty > 0; })
      .map(function (t) {
        const variantId = variantIdFor(t, forExperience);
        if (!variantId) {
          throw new Error('No Shopify variant for "' + booking.productName + '" / "' + t.slug + '".');
        }
        return {
          id: variantId,
          quantity: t.qty,
          properties: {
            // Machine metadata (hidden from the customer — leading underscore)
            '_booking_hold_token': booking.holdToken || '',
            '_booking_product_id': booking.productId,
            '_booking_product_slug': booking.productSlug,
            '_booking_location_id': booking.locationId,
            '_booking_ticket_slug': t.slug,
            '_booking_date': booking.date,
            '_booking_time': booking.time,
            // Customer-facing (shown in cart + checkout)
            'Experience': booking.productName,
            'Location': booking.locationName,
            'Date': booking.date,
            'Time': booking.time,
          },
        };
      });

    if (items.length === 0) throw new Error('No tickets selected');

    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    });

    if (!resp.ok) {
      const body = await resp.json().catch(function () { return {}; });
      throw new Error(body.description || 'Failed to add to cart');
    }

    return resp.json();
  }

  return { addToCart: addToCart, variantIdFor: variantIdFor };
})();
/**
 * Location Picker Component
 */
const LocationPicker = (function () {
  function render(container, locations, onSelect) {
    var html = '<div class="pb-step">';
    html += '<h2 class="pb-step-title">Select Location</h2>';
    html += '<div class="pb-location-grid">';

    locations.forEach(function (loc) {
      html += '<button class="pb-location-card" data-id="' + loc.id + '">';
      html += '<span class="pb-location-name">' + escapeHtml(loc.name) + '</span>';
      if (loc.address) {
        html += '<span class="pb-location-address">' + escapeHtml(loc.address) + '</span>';
      }
      html += '</button>';
    });

    html += '</div></div>';
    container.innerHTML = html;

    container.querySelectorAll('.pb-location-card').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-id');
        var loc = locations.find(function (l) { return String(l.id) === id; });
        onSelect(loc);
      });
    });
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  return { render: render };
})();
/**
 * Product Picker Component
 */
const ProductPicker = (function () {
  function render(container, products, onSelect) {
    var html = '<div class="pb-step">';
    html += '<h2 class="pb-step-title">Select Experience</h2>';
    html += '<div class="pb-product-grid">';

    products.forEach(function (prod) {
      var priceFrom = getLowestPrice(prod);
      html += '<button class="pb-product-card" data-id="' + prod.id + '">';
      html += '<div class="pb-product-info">';
      html += '<span class="pb-product-name">' + escapeHtml(prod.name) + '</span>';
      if (prod.description) {
        html += '<p class="pb-product-desc">' + escapeHtml(prod.description) + '</p>';
      }
      html += '<div class="pb-product-meta">';
      if (prod.duration) {
        html += '<span class="pb-product-duration">' + prod.duration + ' min</span>';
      }
      if (priceFrom !== null) {
        html += '<span class="pb-product-price">From &pound;' + priceFrom.toFixed(2) + '</span>';
      }
      html += '</div>';
      if (prod.ageRestrictions) {
        html += '<span class="pb-age-notice">' + escapeHtml(prod.ageRestrictions) + '</span>';
      }
      html += '</div>';
      html += '</button>';
    });

    html += '</div></div>';
    container.innerHTML = html;

    container.querySelectorAll('.pb-product-card').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-id');
        var prod = products.find(function (p) { return String(p.id) === id; });
        onSelect(prod);
      });
    });
  }

  function getLowestPrice(product) {
    if (!product.ticketTypes || product.ticketTypes.length === 0) return null;
    var prices = product.ticketTypes.map(function (t) { return (t.onlinePrice || t.doorPrice || 0) / 100; });
    return Math.min.apply(null, prices);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  return { render: render };
})();
/**
 * Date Picker Component — Month calendar view
 */
const DatePicker = (function () {
  var currentMonth = null;
  var currentYear = null;
  var sessionsCache = {};

  function render(container, state, onSelect) {
    var today = new Date();
    if (!currentMonth) currentMonth = today.getMonth();
    if (!currentYear) currentYear = today.getFullYear();

    drawCalendar(container, state, onSelect);
    loadSessions(container, state, onSelect);
  }

  function drawCalendar(container, state, onSelect) {
    var monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    var dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    var html = '<div class="pb-step">';
    html += '<h2 class="pb-step-title">Pick a Date</h2>';
    html += '<div class="pb-calendar">';

    // Month navigation
    html += '<div class="pb-cal-header">';
    html += '<button class="pb-cal-nav pb-cal-prev" aria-label="Previous month">&lsaquo;</button>';
    html += '<span class="pb-cal-month">' + monthNames[currentMonth] + ' ' + currentYear + '</span>';
    html += '<button class="pb-cal-nav pb-cal-next" aria-label="Next month">&rsaquo;</button>';
    html += '</div>';

    // Day headers
    html += '<div class="pb-cal-grid">';
    dayNames.forEach(function (d) {
      html += '<div class="pb-cal-day-header">' + d + '</div>';
    });

    // Calculate days
    var firstDay = new Date(currentYear, currentMonth, 1);
    var lastDay = new Date(currentYear, currentMonth + 1, 0);
    // getDay() returns 0=Sun; we want Mon=0
    var startDow = (firstDay.getDay() + 6) % 7;
    var today = new Date();
    today.setHours(0, 0, 0, 0);

    // Empty cells before first day
    for (var i = 0; i < startDow; i++) {
      html += '<div class="pb-cal-cell pb-cal-empty"></div>';
    }

    var cacheKey = currentYear + '-' + padTwo(currentMonth + 1);
    var sessions = sessionsCache[cacheKey] || {};

    for (var d = 1; d <= lastDay.getDate(); d++) {
      var dateStr = currentYear + '-' + padTwo(currentMonth + 1) + '-' + padTwo(d);
      var cellDate = new Date(currentYear, currentMonth, d);
      var isPast = cellDate < today;
      var dayData = sessions[dateStr];
      var hasAvailability = dayData && dayData.available > 0;
      var isSoldOut = dayData && dayData.available === 0;
      var isLoading = !dayData && !isPast;

      var classes = 'pb-cal-cell';
      if (isPast) classes += ' pb-cal-past';
      else if (isSoldOut) classes += ' pb-cal-sold-out';
      else if (hasAvailability) classes += ' pb-cal-available';
      else if (isLoading) classes += ' pb-cal-loading';

      if (state.selectedDate === dateStr) classes += ' pb-cal-selected';

      html += '<button class="' + classes + '" data-date="' + dateStr + '"';
      if (isPast || isSoldOut || isLoading) html += ' disabled';
      html += '>' + d;
      html += '</button>';
    }

    html += '</div></div></div>';
    container.innerHTML = html;

    // Event listeners
    var prevBtn = container.querySelector('.pb-cal-prev');
    var nextBtn = container.querySelector('.pb-cal-next');

    prevBtn.addEventListener('click', function () {
      currentMonth--;
      if (currentMonth < 0) { currentMonth = 11; currentYear--; }
      drawCalendar(container, state, onSelect);
      loadSessions(container, state, onSelect);
    });

    nextBtn.addEventListener('click', function () {
      currentMonth++;
      if (currentMonth > 11) { currentMonth = 0; currentYear++; }
      drawCalendar(container, state, onSelect);
      loadSessions(container, state, onSelect);
    });

    container.querySelectorAll('.pb-cal-cell.pb-cal-available').forEach(function (btn) {
      btn.addEventListener('click', function () {
        onSelect(btn.getAttribute('data-date'));
      });
    });
  }

  function loadSessions(container, state, onSelect) {
    var start = currentYear + '-' + padTwo(currentMonth + 1) + '-01';
    var lastDay = new Date(currentYear, currentMonth + 1, 0);
    var end = currentYear + '-' + padTwo(currentMonth + 1) + '-' + padTwo(lastDay.getDate());
    var cacheKey = currentYear + '-' + padTwo(currentMonth + 1);

    if (sessionsCache[cacheKey]) return;

    PenderynAPI.getSessions(start, end, state.location.id, state.product.id)
      .then(function (data) {
        var byDate = {};
        // Initialize all dates as unavailable
        for (var d = 1; d <= lastDay.getDate(); d++) {
          var ds = currentYear + '-' + padTwo(currentMonth + 1) + '-' + padTwo(d);
          byDate[ds] = { available: 0, sessions: [] };
        }
        // Fill in available dates (filter by selected product, map field names)
        (data || []).forEach(function (session) {
          // Filter to selected product
          var sid = session.productId || session.product;
          if (sid && state.product && state.product.id && String(sid) !== String(state.product.id)) return;

          if (!byDate[session.date]) {
            byDate[session.date] = { available: 0, sessions: [] };
          }
          var capacity = session.remaining || session.availableCapacity || 0;
          byDate[session.date].available += capacity;
          byDate[session.date].sessions.push(session);
        });
        sessionsCache[cacheKey] = byDate;
        drawCalendar(container, state, onSelect);
      })
      .catch(function () {
        // Silently fail, leave as loading
      });
  }

  function reset() {
    currentMonth = null;
    currentYear = null;
    sessionsCache = {};
  }

  function getSessionsForDate(date) {
    var parts = date.split('-');
    var cacheKey = parts[0] + '-' + parts[1];
    var dayData = sessionsCache[cacheKey] && sessionsCache[cacheKey][date];
    return dayData ? dayData.sessions : [];
  }

  function padTwo(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  return { render: render, reset: reset, getSessionsForDate: getSessionsForDate };
})();
/**
 * Time Picker Component
 */
const TimePicker = (function () {
  function render(container, sessions, selectedTime, onSelect) {
    var html = '<div class="pb-step">';
    html += '<h2 class="pb-step-title">Pick a Time</h2>';
    html += '<div class="pb-time-grid">';

    if (!sessions || sessions.length === 0) {
      html += '<p class="pb-no-slots">No time slots available for this date.</p>';
    } else {
      sessions.forEach(function (session) {
        var available = session.remaining != null ? session.remaining : session.availableCapacity;
        var total = session.capacity || session.totalCapacity;
        var soldOut = session.isSoldOut || available === 0;
        var lowStock = available > 0 && available <= 4;

        var classes = 'pb-time-slot';
        if (soldOut) classes += ' pb-time-sold-out';
        if (selectedTime === session.time) classes += ' pb-time-selected';

        html += '<button class="' + classes + '" data-time="' + session.time + '"';
        if (soldOut) html += ' disabled';
        html += '>';
        html += '<span class="pb-time-label">' + formatTime(session.time) + '</span>';
        if (soldOut) {
          html += '<span class="pb-time-status pb-time-status-sold">Sold out</span>';
        } else if (lowStock) {
          html += '<span class="pb-time-status pb-time-status-low">' + available + ' spot' + (available !== 1 ? 's' : '') + ' remaining</span>';
        } else {
          html += '<span class="pb-time-status pb-time-status-ok">' + available + ' available</span>';
        }
        html += '</button>';
      });
    }

    html += '</div></div>';
    container.innerHTML = html;

    container.querySelectorAll('.pb-time-slot:not(.pb-time-sold-out)').forEach(function (btn) {
      btn.addEventListener('click', function () {
        onSelect(btn.getAttribute('data-time'));
      });
    });
  }

  // A start time saved as "12" rather than "12:00" used to print "12:undefined pm".
  // Treat missing or short minutes as the top of the hour.
  function formatTime(time) {
    // Times have been entered as "12", "12:30" and "12.30". Accept all three
    // rather than reading "12.30" as midday, which would send people an hour out.
    var parts = String(time || '').split(/[:.]/);
    var h = parseInt(parts[0], 10);
    if (!isFinite(h)) return String(time || '');
    var m = (parts[1] || '0').replace(/\D/g, '').slice(0, 2);
    m = (m === '' ? '00' : m.length === 1 ? '0' + m : m);
    if (h === 0) return '12:' + m + ' am';
    if (h < 12) return h + ':' + m + ' am';
    if (h === 12) return '12:' + m + ' pm';
    return (h - 12) + ':' + m + ' pm';
  }

  return { render: render };
})();
/**
 * Ticket Selector Component — quantity pickers with live pricing
 */
const TicketSelector = (function () {
  function render(container, product, tickets, availableCapacity, onChange) {
    var html = '<div class="pb-step">';
    html += '<h2 class="pb-step-title">Select Tickets</h2>';
    html += '<div class="pb-ticket-list">';

    product.ticketTypes.forEach(function (type, index) {
      var ticket = tickets[index] || { qty: 0 };
      var price = (type.onlinePrice || type.doorPrice || 0) / 100;
      var min = 0;
      var max = type.maxQuantity || 20;

      html += '<div class="pb-ticket-row">';
      html += '<div class="pb-ticket-info">';
      html += '<span class="pb-ticket-name">' + escapeHtml(type.name) + '</span>';
      html += '<span class="pb-ticket-price">&pound;' + price.toFixed(2) + '</span>';
      html += '</div>';
      html += '<div class="pb-ticket-controls">';
      html += '<button class="pb-ticket-btn pb-ticket-minus" data-index="' + index + '"'
        + (ticket.qty <= min ? ' disabled' : '') + '>&minus;</button>';
      html += '<span class="pb-ticket-qty">' + ticket.qty + '</span>';
      html += '<button class="pb-ticket-btn pb-ticket-plus" data-index="' + index + '"'
        + (ticket.qty >= max ? ' disabled' : '') + '>+</button>';
      html += '</div>';
      html += '</div>';
    });

    html += '</div>';

    // Capacity warning
    var totalGuests = tickets.reduce(function (sum, t) { return sum + t.qty; }, 0);
    if (availableCapacity !== null && availableCapacity <= 4 && availableCapacity > 0) {
      html += '<div class="pb-capacity-warning">' + availableCapacity + ' spot' + (availableCapacity !== 1 ? 's' : '') + ' remaining</div>';
    }

    // Total
    var total = calculateTotal(product.ticketTypes, tickets);
    html += '<div class="pb-ticket-total">';
    html += '<span>Total</span>';
    html += '<span class="pb-ticket-total-price">&pound;' + total.toFixed(2) + '</span>';
    html += '</div>';

    html += '</div>';
    container.innerHTML = html;

    // Event listeners
    container.querySelectorAll('.pb-ticket-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-index'), 10);
        var type = product.ticketTypes[idx];
        var min = 0;
        if (tickets[idx].qty > min) {
          tickets[idx].qty--;
          onChange(tickets);
        }
      });
    });

    container.querySelectorAll('.pb-ticket-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-index'), 10);
        var type = product.ticketTypes[idx];
        var max = type.maxQuantity || 20;
        if (tickets[idx].qty < max) {
          tickets[idx].qty++;
          onChange(tickets);
        }
      });
    });
  }

  function calculateTotal(ticketTypes, tickets) {
    var total = 0;
    ticketTypes.forEach(function (type, i) {
      var price = (type.onlinePrice || type.doorPrice || 0) / 100;
      total += price * (tickets[i] ? tickets[i].qty : 0);
    });
    return total;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  return { render: render, calculateTotal: calculateTotal };
})();
/**
 * Promo Code Component
 */
const PromoCode = (function () {
  function render(container, promoState, onApply, onRemove) {
    var html = '<div class="pb-promo">';

    if (promoState.applied) {
      html += '<div class="pb-promo-applied">';
      html += '<span class="pb-promo-badge">' + escapeHtml(promoState.code) + '</span>';
      html += '<span class="pb-promo-discount">' + escapeHtml(promoState.description || 'Discount applied') + '</span>';
      html += '<button class="pb-promo-remove" aria-label="Remove promo code">&times;</button>';
      html += '</div>';
    } else {
      html += '<div class="pb-promo-input-row">';
      html += '<input type="text" class="pb-promo-input" placeholder="Promo code" value="' + escapeHtml(promoState.code || '') + '">';
      html += '<button class="pb-promo-apply"' + (promoState.loading ? ' disabled' : '') + '>';
      html += promoState.loading ? 'Checking...' : 'Apply';
      html += '</button>';
      html += '</div>';
      if (promoState.error) {
        html += '<p class="pb-promo-error">' + escapeHtml(promoState.error) + '</p>';
      }
    }

    html += '</div>';
    container.innerHTML = html;

    var applyBtn = container.querySelector('.pb-promo-apply');
    var input = container.querySelector('.pb-promo-input');
    var removeBtn = container.querySelector('.pb-promo-remove');

    if (applyBtn && input) {
      applyBtn.addEventListener('click', function () {
        onApply(input.value.trim());
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          onApply(input.value.trim());
        }
      });
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        onRemove();
      });
    }
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  return { render: render };
})();
/**
 * Summary / Confirmation Component
 */
const Summary = (function () {
  function render(container, state, onAddToCart, onContinueShopping) {
    if (state.confirmed) {
      renderConfirmation(container, state, onContinueShopping);
      return;
    }
    renderSummary(container, state, onAddToCart);
  }

  function renderSummary(container, state, onAddToCart) {
    var tickets = state.tickets.filter(function (t) { return t.qty > 0; });
    var total = TicketSelector.calculateTotal(state.product.ticketTypes, state.tickets);

    // Apply promo discount if any
    var discount = 0;
    if (state.promo && state.promo.applied && state.promo.discount) {
      if (state.promo.discount.type === 'percentage') {
        discount = total * (state.promo.discount.value / 100);
      } else {
        discount = state.promo.discount.value;
      }
    }
    var finalTotal = Math.max(0, total - discount);

    var dateStr = formatDate(state.selectedDate);

    var html = '<div class="pb-step">';
    html += '<h2 class="pb-step-title">Booking Summary</h2>';
    html += '<div class="pb-summary">';

    html += '<div class="pb-summary-row">';
    html += '<span class="pb-summary-label">Location</span>';
    html += '<span class="pb-summary-value">' + escapeHtml(state.location.name) + '</span>';
    html += '</div>';

    html += '<div class="pb-summary-row">';
    html += '<span class="pb-summary-label">Experience</span>';
    html += '<span class="pb-summary-value">' + escapeHtml(state.product.name) + '</span>';
    html += '</div>';

    html += '<div class="pb-summary-row">';
    html += '<span class="pb-summary-label">Date &amp; Time</span>';
    html += '<span class="pb-summary-value">' + dateStr + ' at ' + formatTime(state.selectedTime) + '</span>';
    html += '</div>';

    html += '<div class="pb-summary-row">';
    html += '<span class="pb-summary-label">Tickets</span>';
    html += '<span class="pb-summary-value">';
    tickets.forEach(function (t, i) {
      if (i > 0) html += '<br>';
      html += t.qty + ' &times; ' + escapeHtml(t.name);
    });
    html += '</span>';
    html += '</div>';

    if (discount > 0) {
      html += '<div class="pb-summary-row pb-summary-discount">';
      html += '<span class="pb-summary-label">Discount (' + escapeHtml(state.promo.code) + ')</span>';
      html += '<span class="pb-summary-value">&minus;&pound;' + discount.toFixed(2) + '</span>';
      html += '</div>';
    }

    html += '<div class="pb-summary-row pb-summary-total">';
    html += '<span class="pb-summary-label">Total</span>';
    html += '<span class="pb-summary-value">&pound;' + finalTotal.toFixed(2) + '</span>';
    html += '</div>';

    html += '</div>';

    if (state.addingToCart) {
      html += '<button class="pb-btn pb-btn-primary pb-btn-full" disabled>Adding to Cart...</button>';
    } else if (state.cartError) {
      html += '<div class="pb-error">' + escapeHtml(state.cartError) + '</div>';
      html += '<button class="pb-btn pb-btn-primary pb-btn-full pb-add-to-cart">Try Again</button>';
    } else {
      html += '<button class="pb-btn pb-btn-primary pb-btn-full pb-add-to-cart">Add to Cart</button>';
    }

    html += '</div>';
    container.innerHTML = html;

    var addBtn = container.querySelector('.pb-add-to-cart');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        onAddToCart(finalTotal);
      });
    }
  }

  function renderConfirmation(container, state, onContinueShopping) {
    var html = '<div class="pb-step pb-confirmation">';
    html += '<div class="pb-confirm-icon">&#10003;</div>';
    html += '<h2 class="pb-step-title">Added to Cart</h2>';
    html += '<p class="pb-confirm-text">Your ' + escapeHtml(state.product.name) + ' at '
      + escapeHtml(state.location.name) + ' on ' + formatDate(state.selectedDate)
      + ' at ' + formatTime(state.selectedTime) + ' has been added to your cart.</p>';
    html += '<p class="pb-confirm-hold">Your booking is held for 15 minutes. Please complete checkout to confirm.</p>';
    html += '<div class="pb-confirm-actions">';
    html += '<a href="/cart" class="pb-btn pb-btn-primary">Go to Cart</a>';
    html += '<button class="pb-btn pb-btn-secondary pb-continue-shopping">Book Another</button>';
    html += '</div>';
    html += '</div>';

    container.innerHTML = html;

    var continueBtn = container.querySelector('.pb-continue-shopping');
    if (continueBtn) {
      continueBtn.addEventListener('click', function () {
        onContinueShopping();
      });
    }
  }

  function formatDate(dateStr) {
    var parts = dateStr.split('-');
    var date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return days[date.getDay()] + ' ' + date.getDate() + ' ' + months[date.getMonth()] + ' ' + parts[0];
  }

  // A start time saved as "12" rather than "12:00" used to print "12:undefined pm".
  // Treat missing or short minutes as the top of the hour.
  function formatTime(time) {
    // Times have been entered as "12", "12:30" and "12.30". Accept all three
    // rather than reading "12.30" as midday, which would send people an hour out.
    var parts = String(time || '').split(/[:.]/);
    var h = parseInt(parts[0], 10);
    if (!isFinite(h)) return String(time || '');
    var m = (parts[1] || '0').replace(/\D/g, '').slice(0, 2);
    m = (m === '' ? '00' : m.length === 1 ? '0' + m : m);
    if (h === 0) return '12:' + m + ' am';
    if (h < 12) return h + ':' + m + ' am';
    if (h === 12) return '12:' + m + ' pm';
    return (h - 12) + ':' + m + ' pm';
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  return { render: render };
})();
/**
 * Penderyn Booking Widget
 * Main entry point — state management and render loop
 */
var PenderynBookingWidget = (function () {
  var STEPS = ['location', 'product', 'date', 'time', 'tickets', 'summary'];
  var STEP_LABELS = ['Location', 'Experience', 'Date', 'Time', 'Tickets', 'Summary'];

  var state = {};
  var rootEl = null;
  var contentEl = null;

  function init(selector) {
    rootEl = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (!rootEl) {
      console.error('PenderynBookingWidget: container not found');
      return;
    }
    rootEl.classList.add('penderyn-booking');
    resetState();
    renderShell();
    goToStep('location');
  }

  function resetState() {
    state = {
      step: 'location',
      locations: [],
      location: null,
      products: [],
      product: null,
      selectedDate: null,
      selectedTime: null,
      tickets: [],
      availableCapacity: null,
      promo: { code: '', applied: false, loading: false, error: null, discount: null, description: '' },
      holdToken: null,
      addingToCart: false,
      cartError: null,
      confirmed: false,
      loading: false,
      error: null,
    };
  }

  function renderShell() {
    var html = '<div class="pb-widget">';
    html += '<div class="pb-progress"></div>';
    html += '<div class="pb-content"></div>';
    html += '<div class="pb-nav"></div>';
    html += '</div>';
    rootEl.innerHTML = html;
    contentEl = rootEl.querySelector('.pb-content');
  }

  function renderProgress() {
    var el = rootEl.querySelector('.pb-progress');
    var currentIdx = STEPS.indexOf(state.step);
    var html = '<div class="pb-progress-bar">';

    STEPS.forEach(function (step, i) {
      var classes = 'pb-progress-step';
      if (i < currentIdx) classes += ' pb-progress-done';
      if (i === currentIdx) classes += ' pb-progress-active';
      html += '<div class="' + classes + '">';
      html += '<div class="pb-progress-dot"><span>' + (i + 1) + '</span></div>';
      html += '<div class="pb-progress-label">' + STEP_LABELS[i] + '</div>';
      html += '</div>';
      if (i < STEPS.length - 1) {
        html += '<div class="pb-progress-line' + (i < currentIdx ? ' pb-progress-line-done' : '') + '"></div>';
      }
    });

    html += '</div>';
    el.innerHTML = html;
  }

  function renderNav() {
    var el = rootEl.querySelector('.pb-nav');
    var currentIdx = STEPS.indexOf(state.step);

    if (state.confirmed) {
      el.innerHTML = '';
      return;
    }

    var html = '';
    if (currentIdx > 0) {
      html += '<button class="pb-btn pb-btn-secondary pb-back-btn">&larr; Back</button>';
    }
    el.innerHTML = html;

    var backBtn = el.querySelector('.pb-back-btn');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        goBack();
      });
    }
  }

  function goToStep(step) {
    state.step = step;
    renderProgress();
    renderNav();
    renderStep();
  }

  function goBack() {
    var idx = STEPS.indexOf(state.step);
    if (idx > 0) {
      // Release hold if going back from summary
      if (state.step === 'summary' && state.holdToken) {
        PenderynAPI.releaseReservation(state.holdToken).catch(function () {});
        state.holdToken = null;
      }
      goToStep(STEPS[idx - 1]);
    }
  }

  function renderStep() {
    switch (state.step) {
      case 'location':
        renderLocationStep();
        break;
      case 'product':
        renderProductStep();
        break;
      case 'date':
        renderDateStep();
        break;
      case 'time':
        renderTimeStep();
        break;
      case 'tickets':
        renderTicketStep();
        break;
      case 'summary':
        renderSummaryStep();
        break;
    }
  }

  // --- Step renderers ---

  function renderLocationStep() {
    if (state.locations.length > 0) {
      LocationPicker.render(contentEl, state.locations, function (loc) {
        state.location = loc;
        state.products = [];
        state.product = null;
        goToStep('product');
      });
      return;
    }

    contentEl.innerHTML = '<div class="pb-loading">Loading locations...</div>';
    PenderynAPI.getLocations()
      .then(function (locations) {
        state.locations = locations;
        LocationPicker.render(contentEl, state.locations, function (loc) {
          state.location = loc;
          state.products = [];
          state.product = null;
          goToStep('product');
        });
      })
      .catch(function (err) {
        contentEl.innerHTML = '<div class="pb-error">Could not load locations. Please try again.<br><button class="pb-btn pb-btn-secondary pb-retry">Retry</button></div>';
        contentEl.querySelector('.pb-retry').addEventListener('click', function () {
          renderLocationStep();
        });
      });
  }

  function renderProductStep() {
    if (state.products.length > 0) {
      ProductPicker.render(contentEl, state.products, function (prod) {
        state.product = prod;
        DatePicker.reset();
        goToStep('date');
      });
      return;
    }

    contentEl.innerHTML = '<div class="pb-loading">Loading experiences...</div>';
    PenderynAPI.getProducts(state.location.id)
      .then(function (products) {
        state.products = products;
        ProductPicker.render(contentEl, state.products, function (prod) {
          state.product = prod;
          DatePicker.reset();
          goToStep('date');
        });
      })
      .catch(function (err) {
        contentEl.innerHTML = '<div class="pb-error">Could not load experiences.<br><button class="pb-btn pb-btn-secondary pb-retry">Retry</button></div>';
        contentEl.querySelector('.pb-retry').addEventListener('click', function () {
          renderProductStep();
        });
      });
  }

  function renderDateStep() {
    DatePicker.render(contentEl, state, function (date) {
      state.selectedDate = date;
      state.selectedTime = null;
      goToStep('time');
    });
  }

  function renderTimeStep() {
    var sessions = DatePicker.getSessionsForDate(state.selectedDate);
    TimePicker.render(contentEl, sessions, state.selectedTime, function (time) {
      state.selectedTime = time;
      // Find capacity for this session
      var session = sessions.find(function (s) { return s.time === time; });
      state.availableCapacity = session ? (session.remaining != null ? session.remaining : session.availableCapacity) : null;
      // Initialize tickets
      state.tickets = state.product.ticketTypes.map(function (type) {
        // shopifyVariantId comes from the booking system, which creates the
        // variant when the experience is saved. The theme's map is only a
        // fallback for anything saved before that existed.
        return { name: type.name, slug: type.slug, qty: 0, shopifyVariantId: type.shopifyVariantId || '' };
      });
      goToStep('tickets');
    });
  }

  function renderTicketStep() {
    var ticketContainer = document.createElement('div');
    /**
     * No promo box here.
     *
     * A code typed in never reached the order: it was checked for display
     * only, never sent with the reservation or into the Shopify cart, and the
     * booking was always created with no discount. So the field invited a code
     * into the one place that could not honour it, and a partner's guest was
     * told their code was invalid.
     *
     * Partner codes belong in Shopify, entered at the payment page, where they
     * apply to bottles as well as tours. PromoCode below is kept for when that
     * is wired up; the booking system's own codes still work on staff bookings
     * taken in the admin.
     */

    contentEl.innerHTML = '';
    contentEl.appendChild(ticketContainer);

    function renderTickets() {
      TicketSelector.render(ticketContainer, state.product, state.tickets, state.availableCapacity, function (tickets) {
        state.tickets = tickets;
        renderTickets();
      });
    }

    renderTickets();

    // Add continue button
    var continueBtn = document.createElement('button');
    continueBtn.className = 'pb-btn pb-btn-primary pb-btn-full pb-continue-btn';
    continueBtn.textContent = 'Continue';
    contentEl.appendChild(continueBtn);

    continueBtn.addEventListener('click', function () {
      var totalGuests = state.tickets.reduce(function (sum, t) { return sum + t.qty; }, 0);
      if (totalGuests === 0) {
        alert('Please select at least one ticket.');
        return;
      }
      // Infant tickets are free, and a basket of nothing but free tickets is
      // not a booking: it would take a seat on the tour and charge nothing.
      // The API refuses it as well; this is so the customer finds out here
      // rather than at checkout.
      var types = (state.product && state.product.ticketTypes) || [];
      var payingGuests = state.tickets.reduce(function (sum, t, i) {
        var type = types[i] || {};
        var price = type.onlinePrice || type.doorPrice || 0;
        return sum + (price > 0 ? t.qty : 0);
      }, 0);
      if (payingGuests === 0) {
        var freeNames = types.filter(function (type, i) {
          return state.tickets[i] && state.tickets[i].qty > 0;
        }).map(function (type) { return type.name; });
        alert(freeNames.join(' and ') + ' go free, so they cannot be booked on their own. '
          + 'Please add at least one paying ticket.');
        return;
      }
      if (state.availableCapacity !== null && totalGuests > state.availableCapacity) {
        alert('Only ' + state.availableCapacity + ' spots available. Please reduce your ticket quantity.');
        return;
      }
      goToStep('summary');
    });
  }

  function renderSummaryStep() {
    Summary.render(contentEl, state, function (finalTotal) {
      addToCart(finalTotal);
    }, function () {
      // Book another — full reset
      resetState();
      renderShell();
      goToStep('location');
    });
  }

  function addToCart(finalTotal) {
    state.addingToCart = true;
    state.cartError = null;
    renderSummaryStep();

    var totalGuests = state.tickets.reduce(function (sum, t) { return sum + t.qty; }, 0);

    // Create reservation hold first — the server generates and returns the hold token
    PenderynAPI.createReservation({
      product: state.product.id,
      location: state.location.id,
      sessionDate: state.selectedDate,
      sessionTime: state.selectedTime,
      guestCount: totalGuests,
    })
      .then(function (hold) {
        state.holdToken = hold.holdToken;

        var booking = {
          productId: state.product.id,
          productSlug: state.product.slug,
          locationId: state.location.id,
          productName: state.product.name,
          locationName: state.location.name,
          date: state.selectedDate,
          time: state.selectedTime,
          tickets: state.tickets,
          total: finalTotal,
          holdToken: hold.holdToken,
        };

        return PenderynCart.addToCart(booking);
      })
      .then(function () {
        state.addingToCart = false;
        state.confirmed = true;
        renderSummaryStep();
        renderNav();
      })
      .catch(function (err) {
        state.addingToCart = false;
        state.cartError = err.message || 'Failed to add to cart. Please try again.';
        renderSummaryStep();
      });
  }


  return { init: init };
})();

// Auto-init if data attribute found
document.addEventListener('DOMContentLoaded', function () {
  var el = document.querySelector('[data-penderyn-booking]');
  if (el) {
    PenderynBookingWidget.init(el);
  }
});
